import { haptic } from '../haptic.js';
import { Check, Checklist, Repeat } from '../icons.js';
import { BlockedBadge, ProjectBadge } from '../project/ProjectChip.js';

import { DueChip } from './Due.js';
import { checklistStats } from './checklist.js';
import { HOLD_MS } from './closing.js';

import type { ChecklistItem, ProjectProgress, SprintKey, Task, TaskStatus } from '@nedelnik/shared';

/**
 * Части карточки, общие для списка и доски: это два взгляда на один набор
 * задач (3.1.2), поэтому и элементы управления у них обязаны быть одни и те же.
 */

export interface TaskHandlers {
  /** Отметка «готово». Пауза перед уходом в «Готово» — забота списка и доски (3.1.8). */
  onClose: (task: Task) => void;
  /**
   * Снять отметку. `to` — статус, который знает экран (отмена во время паузы);
   * без него задача вернётся туда, откуда её закрыли на этом устройстве (3.1.9).
   */
  onReopen: (task: Task, to?: TaskStatus) => void;
  onShiftStatus: (task: Task, direction: -1 | 1) => void;
  onShiftSprint: (task: Task, direction: -1 | 1) => void;
  /**
   * Назначить задаче из Входящих спринт. Отдельно от `onShiftSprint`, потому
   * что у задачи без недели сдвигать нечего — ей выбирают, куда встать
   * (3.4.6). Спринт приходит ключом, а не сдвигом: меню одно и то же в списке
   * и в карточке (3.5.6), а карточка про показанный период ничего не знает.
   */
  onPlan: (task: Task, sprint: SprintKey | null) => void;
  /**
   * Перенос свайпом (3.1.12): в спринт или во Входящие (`null`). Отличается от
   * `onPlan` плашкой отмены: строка уходит с экрана сразу, и отменить перенос
   * больше негде.
   */
  onMove: (task: Task, sprint: SprintKey | null) => void;
  /**
   * Удаление без карточки (3.1.13): бросок строки в корзину, свайп влево во
   * Входящих. Тоже с плашкой отмены — задача уходит с экрана сразу.
   */
  onDelete: (task: Task) => void;
  /** Открыть панель задачи поверх списка, не уводя со спринта (3.5.1). */
  onOpen: (task: Task) => void;
  /** Перейти к проекту целиком — его задачи из всех спринтов (3.8.6, 3.8.8). */
  onOpenProject: (projectId: string) => void;
  /** Отфильтровать по тегу прямо со строки (3.10.3). */
  onPickTag: (tag: string) => void;
}

/** Всё, что нужно строке и карточке помимо самой задачи. */
export interface TaskExtras {
  tags: ReadonlyMap<string, string[]>;
  checklist: ReadonlyMap<string, ChecklistItem[]>;
  today: string;
  /** Показанная неделя: из неё берутся подписи кнопок «в спринт» (3.4.6). */
  shownSprint: SprintKey;
  /** Текущий спринт: от него считаются «текущая» и «следующая» в подписях свайпа. */
  currentSprint: SprintKey;
  /** Задачи по идентификатору: отсюда чип достаёт проект вложенной задачи. */
  byId: ReadonlyMap<string, Task>;
  /**
   * Прогресс проектов, посчитанный разом на весь экран. Считать его в строке
   * значило бы проходить по всем задачам на каждую из них.
   */
  progress: ReadonlyMap<string, ProjectProgress>;
  /** Кого держит незакрытая зависимость (3.9.4). */
  blocked: ReadonlySet<string>;
}

/**
 * Нажатие по свободному месту виджета открывает задачу.
 *
 * Раньше попасть надо было ровно в заголовок, и на телефоне это оказалось
 * неудобно. Обернуть весь виджет в кнопку нельзя: внутри живут степперы,
 * галочка и теги, а кнопка внутри кнопки — сломанная разметка и неработающие
 * нажатия (грабли прототипа, записаны на этапе 10).
 *
 * Поэтому обработчик висит на самом виджете и пропускает нажатия, попавшие в
 * любой элемент управления. Доступность при этом не страдает: заголовок
 * остаётся кнопкой, и именно он озвучивается и попадает в обход с клавиатуры,
 * — а увеличенная площадь достаётся только указателю.
 */
export function openOnBlankClick(event: React.MouseEvent<HTMLElement>, open: () => void): void {
  const target = event.target as HTMLElement | null;
  if (target?.closest('button, a, input, textarea, select, label') !== null) return;
  open();
}

/**
 * Полоса паузы закрытия (3.1.8): проходит строку за то время, что задача
 * стоит на месте.
 *
 * Отдельный элемент, а не `::after` от класса строки: на iPhone полоса
 * псевдоэлемента у части строк не запускалась вовсе (замечание владельца
 * 2026-10-18, задача в бэклоге при задачах в работе), а элемент, вставленный
 * в момент закрытия, начинает анимацию при вставке — так же сделана полоса
 * плашки отмены, и она работала. Длительность — из `HOLD_MS`, той же цифры,
 * что держит задачу на месте.
 */
export function ClosingTimer(): React.ReactElement {
  return (
    <span
      className="row__timer"
      aria-hidden="true"
      style={{ animationDuration: `${String(HOLD_MS)}ms` }}
    />
  );
}

/**
 * Полоса удержания до кнопки «Удалить» (3.1.14): та же полоса, что у паузы
 * закрытия, цветом опасного действия. На iPhone вибрации в момент, когда
 * кнопка появится, не будет, и дойти до неё глазами — единственный способ
 * знать, сколько ещё держать.
 */
export function DeleteHoldTimer({ ms }: { ms: number }): React.ReactElement {
  return (
    <span
      className="row__timer row__timer--delete"
      aria-hidden="true"
      style={{ animationDuration: `${String(ms)}ms` }}
    />
  );
}

/**
 * Отметка «готово» одним нажатием. Степпер сюда же и доедет, но за два шага, а
 * закрытие задачи — самое частое действие в продукте.
 *
 * Заблокированную задачу закрыть можно (3.9.5): блокировка — подсказка, а не
 * запрет, и здесь нет ни одной проверки, которая бы это нажатие отменяла.
 */
export function DoneToggle({
  task,
  done = task.status === 'done',
  onToggle,
}: {
  task: Task;
  /**
   * Стоит ли галочка. По умолчанию — по данным, но список и доска передают то,
   * что видит человек: отменённое закрытие доезжает до данных не мгновенно.
   */
  done?: boolean;
  onToggle: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className="iconbtn check"
      aria-label={done ? 'Снять отметку «готово»' : 'Отметить готовой'}
      aria-pressed={done}
      onClick={() => {
        haptic();
        onToggle();
      }}
    >
      <Check />
    </button>
  );
}

/**
 * Теги задачи. Показаны с самого начала не ради красоты: `#тег` из строки
 * быстрой записи уходит из заголовка (3.3.3), и без этих чипов запись выглядит
 * так, будто часть текста пропала.
 *
 * Чип — кнопка: клик по тегу фильтрует (3.10.3), и делать для этого отдельный
 * элемент значило бы иметь на экране два разных тега, выглядящих одинаково.
 */
export function TaskTags({
  tags,
  onPick,
}: {
  tags: readonly string[];
  onPick: (tag: string) => void;
}): React.ReactElement | null {
  if (tags.length === 0) return null;

  /*
    Тегов рисуется столько, сколько их есть, а сколько поместится — решает
    раскладка: лишние уезжают на скрытую вторую строку целиком (см. `.tags` в
    стилях). Счётчика «+2» здесь больше нет: он обещал точность, которой у
    него не было, — число считалось по ограничению в коде, а прятала теги
    ширина экрана. Полный список тегов у задачи виден в её карточке, а по
    всем задачам — в ряду тегов над списком.
  */
  return (
    <span className="tags">
      {tags.map((tag) => (
        <button key={tag} type="button" className="tag tag--pick" onClick={() => onPick(tag)}>
          #{tag}
        </button>
      ))}
    </span>
  );
}

/**
 * Заполненность чек-листа снаружи: сколько пунктов закрыто из скольких (3.7.3).
 *
 * Показывается только когда пункты есть — незаполненное поле не занимает места
 * (3.5.4). Пустой счётчик `0/0` сообщал бы ровно ничего.
 *
 * Полоски прогресса здесь больше нет: она повторяла то же число графически и
 * занимала в строке места больше, чем сам счётчик. В карточке задачи полоска
 * осталась — там место есть, и там она к месту.
 */
export function ChecklistBadge({
  items,
}: {
  items: readonly ChecklistItem[];
}): React.ReactElement | null {
  const stats = checklistStats(items);
  if (stats.total === 0) return null;

  const label = `Чек-лист: ${String(stats.done)} из ${String(stats.total)}`;

  return (
    <span className="mark" role="img" aria-label={label} title={label}>
      <Checklist />
      {stats.done}/{stats.total}
    </span>
  );
}

/**
 * Повторяющаяся задача (3.19.1): серый значок без подписи сразу за сроком.
 * Сигналить ему нечем — повтор не торопит и не мешает, — а период виден в
 * карточке. Только у головы серии: прошлый экземпляр уже просто история.
 */
function RepeatMark({ task }: { task: Task }): React.ReactElement | null {
  if (task.repeat == null) return null;
  const label = task.repeat === 'month' ? 'Повторяется каждый месяц' : 'Повторяется';
  return (
    <span className="mark" title={label} aria-label={label}>
      <Repeat />
    </span>
  );
}

/**
 * Полоса отметок под заголовком: срок, блокировка, проект, чек-лист, теги.
 *
 * Одна строка и только одна: пять отметок словами вытесняли заголовок и
 * растягивали строку задачи на три ряда. Поэтому всё, что можно, — значок со
 * счётчиком, а теги стоят последними и занимают то, что осталось.
 *
 * Порядок не случаен: сначала то, что торопит (срок), потом то, что мешает
 * (блокировка), потом устройство задачи (проект, чек-лист) и лишь затем
 * группировка (теги). Обрезается всегда хвост, то есть наименее срочное.
 *
 * Один компонент на список и на доску — иначе отметка, добавленная в одном
 * формате, тихо отсутствует в другом, и «на доске этого не видно» становится
 * дефектом, который находят глазами.
 */
export function TaskMarks({
  task,
  extras,
  handlers,
  className,
}: {
  task: Task;
  extras: TaskExtras;
  handlers: TaskHandlers;
  className: string;
}): React.ReactElement | null {
  const progress = extras.progress.get(task.id);
  const checklist = extras.checklist.get(task.id) ?? [];
  const tags = extras.tags.get(task.id) ?? [];

  /*
   * Пустого ряда отметок не бывает вовсе.
   *
   * Раньше `span` рисовался всегда, и у задачи без единой отметки под
   * заголовком оставалась пустая полоса высотой в отступ. На экране это
   * читалось как неровные карточки: у одной задачи «лишнее» место, у соседней
   * нет. Условия здесь повторяют условия самих значков — каждый из них умеет
   * не рисоваться, но контейнер об этом не знал.
   */
  const empty =
    task.due === null &&
    task.repeat == null &&
    !extras.blocked.has(task.id) &&
    (progress === undefined || progress.total === 0) &&
    checklist.length === 0 &&
    tags.length === 0;

  if (empty) return null;

  return (
    <span className={className}>
      <DueChip task={task} today={extras.today} />
      <RepeatMark task={task} />
      <BlockedBadge blocked={extras.blocked.has(task.id)} />
      <ProjectBadge progress={progress} />
      <ChecklistBadge items={checklist} />
      <TaskTags tags={tags} onPick={handlers.onPickTag} />
    </span>
  );
}
